import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";

import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../components/data/Table";

import {
  Tabs,
  Tab,
  Box,
  Button,
} from "@mui/material";

const ManageBills = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [excursions, setExcursions] = useState([]);
  const [selectedExcursionId, setSelectedExcursionId] = useState("");

  const [tempBills, setTempBills] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);

  const [loadingPage, setLoadingPage] = useState(true);
  const [pageError, setPageError] = useState("");

  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  useEffect(() => {
    const init = async () => {
      if (!token || !user) {
        setLoadingPage(false);
        return;
      }

      try {
        setLoadingPage(true);
        setPageError("");

        const excursionRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          { headers }
        );

        const data = Array.isArray(excursionRes.data) ? excursionRes.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          const activeId = String(user.excursion.excursion_id);
          const activeExc = data.find(
            (e) => String(e.excursion_id) === activeId
          );

          if (activeExc) {
            setSelectedExcursionId(activeId);
            return;
          }
        }

        if (data.length > 0) {
          setSelectedExcursionId(String(data[0].excursion_id));
        }
      } catch (err) {
        console.error("Failed to load excursions:", err);
        setPageError(t("manage_bills.errors.load_excursions"));
      } finally {
        setLoadingPage(false);
      }
    };

    init();
  }, [user, token, headers, t]);

  const selectedExcursionData = excursions.find(
    (exc) => String(exc.excursion_id) === String(selectedExcursionId)
  );

  const isExcursionLocked =
    selectedExcursionData?.status?.toLowerCase() !== "live";

  const fetchTempBills = async () => {
    if (!token || !user || !selectedExcursionId) return;

    try {
      setPageError("");

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/temp-bills/pending`,
        { headers }
      );

      const cleaned = (Array.isArray(res.data) ? res.data : [])
        .filter(
          (bill) =>
            String(bill.excursion_id) === String(selectedExcursionId)
        )
        .map((bill) => ({
          ...bill,
          payers: bill.payers || [],
          beneficiaries: bill.beneficiaries || [],
        }));

      setTempBills(cleaned);

      if (selectedTab >= cleaned.length) {
        setSelectedTab(0);
      }
    } catch (err) {
      console.error("Failed to fetch temp bills:", err);
      setTempBills([]);
      setPageError(t("manage_bills.errors.load_bills"));
    }
  };

  useEffect(() => {
    if (user && token && selectedExcursionId) {
      fetchTempBills();
    }
  }, [user, token, selectedExcursionId]);

  const handleApprove = async (which_id) => {
    if (isExcursionLocked) {
      alert(t("manage_bills.errors.excursion_locked"));
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/temp-bills/${which_id}/approve`,
        {
          decision_text: `${user.name} ${user.surname} approved`,
        },
        { headers }
      );

      alert(t("manage_bills.alerts.approve_success"));
      fetchTempBills();
    } catch (err) {
      console.error("Approve error:", err);
      alert(t("manage_bills.alerts.approve_failed"));
    }
  };

  const handleReject = async (which_id) => {
    if (isExcursionLocked) {
      alert(t("manage_bills.errors.excursion_locked"));
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/temp-bills/${which_id}/reject`,
        {
          decision_text: `${user.name} ${user.surname} rejected`,
        },
        { headers }
      );

      alert(t("manage_bills.alerts.reject_success"));
      fetchTempBills();
    } catch (err) {
      console.error("Reject error:", err);
      alert(t("manage_bills.alerts.reject_failed"));
    }
  };

  const selectedBill = tempBills[selectedTab] || null;

  const payerRows = useMemo(() => {
    if (!selectedBill) return [];
    return selectedBill.payers.map((p) => ({
      member: `${p.name} ${p.surname}`,
      share: p.share,
    }));
  }, [selectedBill]);

  const beneficiaryRows = useMemo(() => {
    if (!selectedBill) return [];
    return selectedBill.beneficiaries.map((b) => ({
      member: `${b.name} ${b.surname}`,
      unit_share: b.unit_share,
      monetary_share: b.monetary_share,
    }));
  }, [selectedBill]);

  const renderMoney = (value) =>
    typeof value === "number" ? value.toFixed(2) : value;

  if (loadingUser || loadingPage) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("manage_bills.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen topNav={<TopNav title={t("manage_bills.title")} mode="main" />}>
        <ScreenContent>
          <Card variant="accent">
            {t("manage_bills.errors.no_access")}
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  const isAdmin = user.role === "admin";
  const isMember = user.role === "member";
  const memberCanView =
    isMember && ["approved", "suspended"].includes(String(user.subscription_status || ""));

  if (!isAdmin && !memberCanView) {
    return (
      <StandardScreen topNav={<TopNav title={t("manage_bills.title")} mode="main" />}>
        <ScreenContent>
          <Card variant="accent">
            {t("manage_bills.errors.no_access")}
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen topNav={<TopNav title={t("manage_bills.title")} mode="main" />}>
      <ScreenContent>
        <Stack gap="5">
          {pageError ? (
            <Card variant="accent">
              <div style={{ color: "var(--color-danger)" }}>{pageError}</div>
            </Card>
          ) : null}

          {isAdmin && isExcursionLocked ? (
            <Card variant="accent">
              <strong>{t("manage_bills.errors.excursion_locked")}</strong>
            </Card>
          ) : null}

          {tempBills.length === 0 ? (
            <Card>{t("manage_bills.empty")}</Card>
          ) : (
            <>
              <Card title={t("manage_bills.pending.title")}>
                <Tabs
                  value={selectedTab}
                  onChange={(e, newVal) => setSelectedTab(newVal)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {tempBills.map((bill, index) => (
                    <Tab
                      key={`${bill.bill_id}-${index}`}
                      value={index}
                      label={t("manage_bills.pending.bill_tab", {
                        id: bill.bill_id,
                      })}
                    />
                  ))}
                </Tabs>
              </Card>

              {selectedBill && (
                <Stack gap="4">
                  <Card
                    title={t("manage_bills.details.title", {
                      id: selectedBill.bill_id,
                    })}
                    subtitle={`${selectedBill.name || ""} ${selectedBill.surname || ""}`}
                  >
                    <Stack gap="2">
                      <div><strong>{t("manage_bills.fields.amount")}:</strong> {selectedBill.amount} {selectedBill.currency}</div>
                      <div><strong>{t("manage_bills.fields.definition")}:</strong> {selectedBill.definition}</div>
                      <div><strong>{t("manage_bills.fields.entry_date")}:</strong> {selectedBill.entry_date}</div>
                      <div><strong>{t("manage_bills.fields.bill_date")}:</strong> {selectedBill.bill_date}</div>
                    </Stack>
                  </Card>

                  <Card title={t("manage_bills.tables.payers.title")}>
                    {payerRows.length > 0 ? (
                      <TableShell>
                        <Table>
                          <thead>
                            <tr>
                              <Th columnIndex={0} sticky>
                                {t("manage_bills.tables.payers.columns.member")}
                              </Th>
                              <Th align="right">
                                {t("manage_bills.tables.payers.columns.share")}
                              </Th>
                            </tr>
                          </thead>
                          <tbody>
                            {payerRows.map((row, index) => (
                              <tr key={`${row.member}-${index}`}>
                                <Td columnIndex={0} sticky>{row.member}</Td>
                                <NumberTd>{renderMoney(row.share)}</NumberTd>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableShell>
                    ) : (
                      <div>{t("manage_bills.empty")}</div>
                    )}
                  </Card>

                  <Card title={t("manage_bills.tables.beneficiaries.title")}>
                    {beneficiaryRows.length > 0 ? (
                      <TableShell>
                        <Table>
                          <thead>
                            <tr>
                              <Th columnIndex={0} sticky>
                                {t("manage_bills.tables.beneficiaries.columns.member")}
                              </Th>
                              <Th align="right">
                                {t("manage_bills.tables.beneficiaries.columns.unit_share")}
                              </Th>
                              <Th align="right">
                                {t("manage_bills.tables.beneficiaries.columns.monetary_share")}
                              </Th>
                            </tr>
                          </thead>
                          <tbody>
                            {beneficiaryRows.map((row, index) => (
                              <tr key={`${row.member}-${index}`}>
                                <Td columnIndex={0} sticky>{row.member}</Td>
                                <NumberTd>{row.unit_share}</NumberTd>
                                <NumberTd>{renderMoney(row.monetary_share)}</NumberTd>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableShell>
                    ) : (
                      <div>{t("manage_bills.empty")}</div>
                    )}
                  </Card>

                  <Card>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleApprove(selectedBill.bill_id)}
                        disabled={isExcursionLocked}
                      >
                        {t("manage_bills.actions.approve")}
                      </Button>

                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleReject(selectedBill.bill_id)}
                        disabled={isExcursionLocked}
                      >
                        {t("manage_bills.actions.reject")}
                      </Button>
                    </Box>
                  </Card>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ManageBills;